import { Injectable, Injector } from '@angular/core';
import { genericRetryWhen, observableEmitDelay } from '@app/core/rxjsutils/rxjs-utilities';
import { ErrorMessages } from '@app/core/utilities/error-messages';
import { delay, finalize, retryWhen, shareReplay, catchError, map, switchMap } from 'rxjs/operators';
import { IObservableStore } from '@app/core/store/infrastructure/base-observable.store';
import { isNullOrUndefined } from 'util';
import { isEmpty } from 'lodash';
import { UserRestrictionService } from '@app/core/services/user/user-restriction.service';
import { IProject } from '@app/core/model/projects/project';
import { BsApiService } from '@app/core/services/bs-api/bs-api-service';
import { IAppSession } from '@app/core/model/appSessions/appSession';
import { BasespaceService, V2DatasetCompactList } from '@bssh/ng-sdk';
import { TrashService } from '@app/core/services/toolbar/trash.service';
import { DeleteOption } from '@app/core/model/delete-options';
import { ResourceType } from '@app/core/model/resource-type';
import { ToastrService } from '@bssh/comp-lib';
import { ResourceCommonUtilityStore } from '../resource/resource.common.utlility.store';
import { IDatasetsState } from './datasets.state';
import { ChangeDatasetQcService } from '../../services/toolbar/change-dataset-qc.service';
import { IDataset } from '../../model/datasets/dataset';
import _ from 'lodash';
import { DatasetTypeSlugs } from '../../model/datasets/dataset-type-slugs';
import { FastqQcStatus } from '../../model/datasets/fastq-qc-status';
import { IBulkUpdateResponse, IErrorState } from '../../model/v2-api/v2-api-wrappers';
import { PageContextService } from '../../services/page-context.service';
import { RestoreService } from '../../services/toolbar/restore.service';
import { ArchiveService } from '../../services/toolbar/archive.service';
import { IApiResponseWrapper } from '../../model/v2-api/v2-api-wrappers';
import { ModalTexts } from '../../../shared/modals/modal-texts';
import { BulkUpdateResponse } from '../../bsshapi/api-wrappers';
import { CodeFeaturesService } from '@app/core/services/user/code-features.service';
import { DownloadService } from '@app/core/services/toolbar/download.service';
import { DownloadUrl } from '@app/core/model/download-url';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { ToastrMessages } from '@app/core/utilities/toastr-messages';

// Interface
export interface IDatasetsStore extends IObservableStore<IDatasetsState> {}

@Injectable({
    providedIn: 'root'
})
export class DatasetsStore extends ResourceCommonUtilityStore<IDatasetsState> implements IDatasetsStore {

    private refreshSubject = new BehaviorSubject<boolean>(false);
    datasetsRefreshed$ = this.refreshSubject.asObservable();

    // pass in the state slices we are interested in...
    constructor(private basespaceApi: BasespaceService, private archiveService: ArchiveService,
        private restoreService: RestoreService, private pageContextService: PageContextService,
        private toastrService: ToastrService, public injector: Injector, private bsApi: BsApiService,
        private userRestrictionService: UserRestrictionService,
        private downloadService: DownloadService, private codeFeaturesService: CodeFeaturesService,
        private changeDatasetQcService: ChangeDatasetQcService, private trashService: TrashService) {
        super(['otherDatasets', 'otherDatasetsOffset', 'otherDatasetsLimit', 'otherDatasetsSortBy', 'otherDatasetsSortDir',
            'fastqDatasets', 'fastqDatasetsOffset', 'fastqDatasetsLimit', 'fastqDatasetsSortBy', 'fastqDatasetsSortDir',
            'selectedDatasets', 'datasetStateError', 'currentUserId',
            'datasetsLoadedForBioSampleId', 'currentUser', 'datasetsLoadedForProjectId',
            'currentDataset'], injector);
    }

    public loadCurrentDataset(id: string, forceLoad: boolean = false) {
        const currentState = this.getState();

        if (currentState && currentState.currentDataset && currentState.currentDataset.Id === id && !forceLoad) {
            this.dispatchCurrentState(currentState);
        } else {
            this.loadingSubject.next(true);

            this.subs.sink = this.basespaceApi.GetV2DatasetsId(id).pipe(
                // Do not make an additional API call if any additional subscribers have subscribed later on
                shareReplay(1),
                // Retry in case of HTTP errors
                retryWhen(genericRetryWhen()),
                // To avoid a Flash of content, maintain a delay
                delay(observableEmitDelay),
                // Stop loading in finalize.
                finalize(() => this.loadingSubject.next(false))
            ).subscribe({
                next: (v2Dataset: IDataset) => {
                    this.setState({ currentDataset: v2Dataset })
                },
                error: error => this.handleError(error, () => ({ ...currentState, datasetStateError: error }))
            })
        }
    }

    public unloadCurrentDataset() {
        this.setState({ currentDataset: undefined });
    }

    /**
     * Calls API to load the fastq datasets associated with the current biosample and cache in the datasets store
     * Assuming tht the current biosample is already populated before we call this.
     */
    public loadFastqDatasetsList(offset: number = 0, limit: number = 50, sortby: any = 'DateCreated',
        sortdir: string = 'Desc', forceLoad: boolean = false): void {

        const currentState = this.getState();
        if (isNullOrUndefined(currentState) || isNullOrUndefined(currentState.datasetsLoadedForBioSampleId)) {
            throw new Error(ErrorMessages.CURRENT_BIOSAMPLE_NOT_AVAILABLE);
        }

        if (currentState && currentState.fastqDatasetsOffset === offset &&
            currentState.fastqDatasetsLimit === limit && currentState.fastqDatasetsSortBy === sortby &&
            currentState.fastqDatasetsSortDir === sortdir && !forceLoad) {

            // if same settings, use the list we already have cached
            this.dispatchCurrentState(currentState);
        } else {
            this.loadingSubject.next(true);

            const requestParams: BasespaceService.GetV2DatasetsParams = {
                offset,
                limit,
                sortby,
                sortdir,
                inputbiosamples: [+currentState.datasetsLoadedForBioSampleId],
                include: ['properties'],
                datasettypes: ['~common.fastq'], // for fastq datasets
                propertyfilters: ['Input.Libraries,Input.Runs,BaseSpace.Metrics.FastQ']
            };

            const datasets$ = this.basespaceApi.GetV2Datasets(requestParams);

            // Subscribe to retrieve
            this.subs.sink = datasets$.pipe(
                // Do not make an additional API call if any additional subscribers have subscribed later on
                shareReplay(1),
                // Retry in case of HTTP errors
                retryWhen(genericRetryWhen()),
                // To avoid a Flash of content, maintain a delay
                delay(observableEmitDelay),
                // Stop loading in finalize.
                finalize(() => this.loadingSubject.next(false))
            ).subscribe({
                next: (response: V2DatasetCompactList) => {
                    this.setState({
                        fastqDatasets: response.Items,
                        fastqDatasetsLimit: response.Paging.Limit,
                        fastqDatasetsOffset: response.Paging.Offset,
                        fastqDatasetsSortBy: response.Paging.SortBy,
                        fastqDatasetsSortDir: response.Paging.SortDir,
                        fastqDatasetsTotalCount: response.Paging.TotalCount,
                        datasetsLoadedForBioSampleId: currentState.datasetsLoadedForBioSampleId,
                        datasetStateError: null,
                        selectedDatasets: null,
                    },
                        DatasetsStoreActions.LoadFastqDatasets);
                },
                error: error => this.handleError(error, () => ({
                    ...currentState,
                    datasetStateError: error,
                    selectedDatasets: null
                }))
            });
        }
    }

    public updateSelectedDatasets(updatedSelections: IDataset[]) {
        this.setState({ selectedDatasets: updatedSelections }, DatasetsStoreActions.UpdateSelectedDatasets);
    }

    /**
     * sets state. Update datasets list and selected datasets
     *
     * @param propertyChange: property that are changing
     * @param updatedDatasets: the datasets acted upon
     * @param actionName: name of action based on service
     * @param error: error in case of partial success ( i.e in case of list of resources)
     * @param forceRefresh: whether to refresh datasets list
     */
    private setStateOnSuccess(propertyChange: object, actionName: DatasetsStoreActions, error: any = null,
        updatedDatasets: IDataset[],
        forceRefresh?: boolean) {

        const { selectedDatasets, otherDatasets, fastqDatasets } = { ...this.getState() };
        let newSelectedDatasets = selectedDatasets;
        let newOtherDatasets = otherDatasets;
        let newFastqDatasets = fastqDatasets;

        if (isNullOrUndefined(newSelectedDatasets) || newSelectedDatasets.length === 0) {
            return;
        }

        if (updatedDatasets) {
            updatedDatasets.forEach(dataset => {
                newSelectedDatasets = this.getUpdatedResourceList(dataset, selectedDatasets, propertyChange) as IDataset[];
                newOtherDatasets = this.getUpdatedResourceList(dataset, otherDatasets, propertyChange) as IDataset[];
                newFastqDatasets = this.getUpdatedResourceList(dataset, fastqDatasets, propertyChange) as IDataset[];
            });
        }

        this.setState({
            selectedDatasets: newSelectedDatasets,
            otherDatasets: newOtherDatasets,
            fastqDatasets: newFastqDatasets,
            datasetStateError: error
        }, actionName);

        this.refreshSubject.next(forceRefresh)
    }

    /**
     * get list of runs based on page context.
     */
    getActiveList(): IDataset[] {
        const { selectedDatasets } = this.getState();

        if (this.pageContextService.isSubResourceDatasetsList()) {
            return selectedDatasets;
        }

        return null;
    }

    canArchive(): boolean {
        return this.isArchiveEnabled();
    }

    archive(): void {
        const archivableDatasets: IDataset[] = this.getArchivableResources() as IDataset[];
        const archiveObservable = this.archiveResources(archivableDatasets);
        this.subs.sink = archiveObservable.subscribe({
            next: (apiResponse: IApiResponseWrapper<BulkUpdateResponse>) => {
                const proccessedResponse = this.processApiResponse(archivableDatasets,
                    apiResponse, ErrorMessages.FAILED_TO_ARCHIVE_DATASETS);
                if (proccessedResponse.totalSuccessCount > 0) {
                    // display toast on success
                    this.toastrService.success(ModalTexts.ARCHIVE.TOAST_SUCCESS.DATASET
                        .replace('$SUCCESS_COUNT', apiResponse.Response.TotalSuccessCount.toString()));
                }

                this.setStateOnSuccess({ IsArchived: true }, DatasetsStoreActions.ArchiveDataset, proccessedResponse.failureError,
                    proccessedResponse.successfulResources as IDataset[], true);
            },
            error: error => {
                this.handleError(error, () => ({ ...this.getState(), runStateError: error }));
            }
        });
        return;
    }

    getArchiveResponse(resourceIds: string[]): Observable<IApiResponseWrapper<BulkUpdateResponse>> {
        return this.archiveService.archive([], resourceIds);
    }

    public canRestore(): boolean {
        return this.isRestoreEnabled();
    }

    public restore(): void {
        const restorableRuns: IDataset[] = this.getRestorableResources() as IDataset[];
        if(restorableRuns.length > 0){

            this.loadingSubject.next(true);

            const restoreObservable = this.restoreResources(restorableRuns);
            this.subs.sink = restoreObservable.subscribe({
                next: (apiResponse: IApiResponseWrapper<BulkUpdateResponse>) => {
                    const proccessedResponse = this.processApiResponse(restorableRuns, apiResponse,
                        ErrorMessages.FAILED_TO_RESTORE_DATASETS);
                    if (proccessedResponse.totalSuccessCount > 0) {
                        // display toast on success
                        this.toastrService.success(ModalTexts.RESTORE.TOAST_SUCCESS.DATASET
                            .replace('$SUCCESS_COUNT', apiResponse.Response.TotalSuccessCount.toString()));
                    }
    
                    this.setStateOnSuccess({ IsArchived: false }, DatasetsStoreActions.RestoreDataset, proccessedResponse.failureError,
                        proccessedResponse.successfulResources as IDataset[], proccessedResponse.totalSuccessCount > 0);
                    
                    this.loadingSubject.next(false);
                },
                error: error => {
                    this.handleError(error, () => ({ ...this.getState(), runStateError: error }));
                    this.loadingSubject.next(false);
                }
            });
        }
        return;
    }

    getRestoreResponse(resourceIds: string[]): Observable<IApiResponseWrapper<BulkUpdateResponse>> {
        return this.restoreService.restore([], resourceIds);
    }

    canDownload(): boolean {
        const activeDatasets = this.getActiveList();
        return activeDatasets && activeDatasets.length > 0 &&
            !_.some(activeDatasets, (dataset) => dataset.IsArchived);
    }

    /**
     * Calls BSSH API to get Dataset Download Uri.
     */
    getDownloadUri(): Observable<DownloadUrl> {
        this.loadingSubject.next(true);
        const activeDatasets = this.getActiveList();
        if (!_.isEmpty(activeDatasets)) {
            const datasetIds = activeDatasets.map((dataset) => dataset.Id);
            return this.downloadService.getDownloadUri(datasetIds, ResourceType.DATASET).pipe(
                finalize(() => {
                    this.loadingSubject.next(false);
                })
            );
        }
        return of(null);
    }

    canCopy(): boolean {
        return !isEmpty(this.getState().selectedDatasets)
            && !this.userRestrictionService.userHasStorageRestrictions();
    }

    copy(destinationProject: IProject): void {
        if (!destinationProject) {
            return;
        }
        let appSessionId = '';
        this.loadingSubject.next(true);
        const selectedDatasets = this.getActiveList();
        if (selectedDatasets.length > 1) {
            // If there is more than one dataset to copy then create a parent appsession and
            // close the appsession once all datasets are copied to the destination project
            const postData = {
                Name: 'Dataset Copy Operation ' + new Date().toLocaleString()
            };
            // using this api as suppose to PostV2Appsessions bcs of mandatory params name and id
            this.subs.sink = this.bsApi.createAppSession(postData).pipe(
                switchMap((response: IAppSession) => {
                    appSessionId = response.Id;
                    return this.copyDatasets(
                        selectedDatasets,
                        destinationProject.Id,
                        destinationProject.Name,
                        appSessionId)
                }),
                switchMap(({ updatedDatasets, copyErrorMessages }) => {
                    const params: BasespaceService.PostV2AppsessionsIdParams = {
                        id: appSessionId,
                        payload: {
                            ExecutionStatus: 'Complete'
                        }
                    };
                    // Close the appsession and continue regardless of success/error
                    return this.basespaceApi.PostV2AppsessionsId(params).pipe(
                        catchError(() => {
                            return of(null);
                        }),
                        map(() => ({ updatedDatasets, copyErrorMessages }))
                    );
                })
            ).subscribe({
                next: ({ updatedDatasets, copyErrorMessages }) => {
                    this.setStateOnSuccess({ Project: destinationProject },
                        DatasetsStoreActions.UpdateSelectedDatasets,
                        copyErrorMessages,
                        updatedDatasets, true);
                    this.loadingSubject.next(false);
                },
                error: (err) => {
                    this.toastrService.error('error', 'An internal error occurred while creating an appsession');
                    this.handleError(err, () => ({ ...this.getState(), datasetStateError: err }));
                    this.loadingSubject.next(false);
                }
            });
        } else {
            // Otherwise just copy the single dataset
            this.copyDatasets(selectedDatasets, destinationProject.Id, destinationProject.Name, null).
                subscribe(({ updatedDatasets, copyErrorMessages }) => {
                    this.setStateOnSuccess({ Project: destinationProject },
                        DatasetsStoreActions.UpdateSelectedDatasets,
                        copyErrorMessages,
                        updatedDatasets, true);
                    this.loadingSubject.next(false);
                });

        }
    }

    // tslint:disable-next-line: max-line-length
    copyDatasets(selectedDatasets: IDataset[], projectId: string, projectName: string, parentAppSessionId: string): Observable<any> {
        if (!selectedDatasets || selectedDatasets.length < 1) {
            return;
        }
        const defaultError = 'An internal error occurred';
        let errorMessage = '';
        let copyErrorMessages = null;

        let datasetsCopiedSuccessfully = 0;
        const updatedDatasets: IDataset[] = [];


        return forkJoin(
            selectedDatasets.map(dataset => {
                return this.copyDatasetToProject(projectId, dataset.Id, parentAppSessionId).pipe(
                    catchError(err => {
                        copyErrorMessages = [];
                        errorMessage = err.data && err.data.ErrorMessage ? err.data.ErrorMessage : err.error.ErrorMessage;

                        let toastMessage = ToastrMessages.COPY_FASTQ_DATASETS_ERROR(dataset.Name, err.error.ErrorCod, errorMessage)

                        this.toastrService.error(toastMessage);

                        copyErrorMessages.push(toastMessage);
                        this.handleError(err, () => ({ ...this.getState(), datasetStateError: err }));
                        return of(undefined);
                    })
                ).pipe(map((response) => {
                    if (response) {
                        datasetsCopiedSuccessfully++;
                        updatedDatasets.push(dataset);
                        return updatedDatasets;
                    }
                    return response;
                }));
            })).pipe(map(() => {
                if (datasetsCopiedSuccessfully > 0) {
                    this.toastrService.success(datasetsCopiedSuccessfully +
                        (datasetsCopiedSuccessfully === 1 ? ' dataset was' : ' datasets were') +
                        ' successfully copied to the project ' + projectName + '.');
                }
                return { updatedDatasets, copyErrorMessages };
            }));

    }

    private copyDatasetToProject(projectId: string, datasetId: string, parentAppSessionId: string): Observable<any> {
        const params: BasespaceService.PostV2DatasetsIdCopyParams = {
            id: datasetId,
            payload: {
                ProjectId: projectId,
                ParentAppSessionId: parentAppSessionId
            }
        };

        return this.basespaceApi.PostV2DatasetsIdCopy(params);
    }

    canTrash(): boolean {
        const activeDatasets = this.getActiveList();
        const { currentUserId } = this.getState();

        return this.trashService.canTrash(activeDatasets, currentUserId);
    }

    /**
     * Calls the TrashService to Trash the Datasets after checking the status
     * @param deleteOption Option to trash the Data Files only
     */
    trash(deleteOption?: DeleteOption): void {

        const activeDatasets = this.getActiveList();

        if (!activeDatasets) {
            throw new Error(ErrorMessages.MISSING_RESOURCE_ERROR);
        }

        const isOwnerOfDatasets = activeDatasets.every(item => this.isResourceOwner(item));
        this.loadingSubject.next(true);

        const DatasetsIds = [];
        activeDatasets.forEach(dataset => {
            DatasetsIds.push(dataset.Id);
        });

        this.trashService.trashResourcesWithV2(DatasetsIds, ResourceType.DATASETS, deleteOption).pipe(
            finalize(() => this.loadingSubject.next(false))
        ).subscribe({
            next: (apiResponses) => {
                const totalSuccessCount = apiResponses.TotalSuccessCount;
                const totalFailureCount = apiResponses.TotalFailureCount;

                // getting datasets to update that were successfully trashed
                const deletedDatasets = [];
                if (totalSuccessCount > 0) {
                    apiResponses.SuccessItems.forEach(item => {
                        const dataset = activeDatasets.find(d => d.Id === item.DeletedDataset.Id);
                        deletedDatasets.push(dataset);
                    });
                }
                // all successfully deleted datasets will have same values for IsFileDataDeleted and IsDeleted
                const isFileDataDeleted = deletedDatasets.length !== 0 ? deletedDatasets[0].IsFileDataDeleted : false;
                const isDeleted = deletedDatasets.length !== 0 ? deletedDatasets[0].IsDeleted : false;

                // getting all error information for dataset that failed deletion
                let trashError = null;
                if (totalFailureCount > 0) {
                    trashError = [];
                    apiResponses.FailureItems.forEach(item => {
                        trashError.push({
                            errorMessage: item.ErrorMessage,
                            data: item
                        });
                    });
                }

                this.setStateOnSuccess(
                    {
                        IsDeleted: isDeleted, IsFileDataDeleted: isFileDataDeleted
                    },
                    DatasetsStoreActions.DeleteDataset,
                    trashError,
                    deletedDatasets,
                    totalSuccessCount > 0
                );

                // For showing toastr message
                if (!trashError || trashError.length === 0) {
                    const multipleDatasetsDeleted = totalSuccessCount > 1 ? true : false;
                    this.toastrService.success(ToastrMessages.TRASH_DATASET_COMPLETE_MESSAGE(deleteOption, isOwnerOfDatasets,
                        multipleDatasetsDeleted));
                }
            },
            error: error => {
                this.handleError(error, () => ({ ...this.getState(), datasetStateError: error }));
                this.loadingSubject.next(false);
            }
        });
    }

    public allAreFastqDatasets(datasets: IDataset[]): boolean {
        if (datasets && datasets.length > 0) {

            return _.every(datasets, d =>
                d.DatasetType.Id === DatasetTypeSlugs.COMMON_FASTQ ||
                (d.DatasetType.ConformsToIds && d.DatasetType.ConformsToIds.indexOf(DatasetTypeSlugs.COMMON_FASTQ) > -1));
        }
        return false;
    }

    public getActiveFastqDatasets(): IDataset[] {
        const activeDatasets = this.getActiveList();
        return this.allAreFastqDatasets(activeDatasets) ? activeDatasets : [];
    }

    public canChangeFastqQc(): boolean {
        const datasets = this.getActiveList();
        return this.changeDatasetQcService.canChangeFastqQc(datasets);
    }

    public changeFastqQc(newStatus: FastqQcStatus, comments?: string): void {
        this.loadingSubject.next(true);
        const fastqDatasets = this.getActiveList();

        if (fastqDatasets && fastqDatasets.length === 0) {
            return;
        }

        this.subs.sink = this.changeDatasetQcService.changeFastqQc(fastqDatasets, newStatus, comments)
            .pipe(finalize(() => this.loadingSubject.next(false)))
            .subscribe({
                next: (apiResponses: IBulkUpdateResponse<IDataset>) => {
                    const totalSuccessCount = apiResponses.TotalSuccessCount;
                    const totalFailureCount = apiResponses.TotalFailureCount;

                    const updatedDatasets = [];
                    if (totalSuccessCount > 0) {
                        apiResponses.SuccessItems.forEach(item => {
                            const dataset = fastqDatasets.find(dataset => dataset.Id === item.Id);
                            updatedDatasets.push(dataset);
                        });
                    }

                    let datasetUpdateError: IErrorState = null;
                    if (totalFailureCount > 0) {
                        datasetUpdateError = {
                            errorMessage: ErrorMessages.FAILED_TO_UPDATE_FASTQ_QC,
                            data: apiResponses.FailureItems
                        };
                    }

                    this.setStateOnSuccess({ Status: newStatus }, DatasetsStoreActions.ChangeFastqQc,
                        datasetUpdateError, updatedDatasets, true);

                    if (apiResponses.TotalFailureCount === 0) {
                        this.toastrService.success(ToastrMessages.QC_STATUS_CHANGE_MESSAGE(apiResponses.TotalSuccessCount, ResourceType.DATASET.toUpperCase()));
                    }
                },
                error: error => {
                    this.handleError(error, () => ({ ...this.getState(), datasetStateError: error }));
                }
            });
    }

    getResourceType(): ResourceType {
        return ResourceType.DATASETS;
      }
}

// The action-names for this store, for logging/tracking.
export enum DatasetsStoreActions {
    LoadOtherDatasets = 'LOAD_OTHER_DATASETS',
    LoadFastqDatasets = 'LOAD_FASTQ_DATASETS',
    ChangeFastqQc = 'CHANGE_FASTQ_QC',
    UpdateSelectedDatasets = 'UPDATE_SELECTED_DATASETS',
    DeleteDataset = 'DELETE_DATASETS',
    ArchiveDataset = 'ARCHIVE_DATASET',
    RestoreDataset = 'RESTORE_DATASET'
}
